/**
 * mutualFundCardConfig.js — the rules the "Tax card configs" list is judged by, on the client.
 *
 * PURE ESM: no Vue, no Vuetify, no axios, no DOM → importable by plain Node, which is the point.
 * `MutualFundCardConfigDialog.vue` is a component and cannot be unit-tested here; the decisions that
 * actually matter — what counts as a legal `configCode`, when a pasted document is a card at all, and
 * what number ends up in `sortOrder` — therefore live in this file and are pinned by
 * `tests/mutualFundCardConfig.test.mjs`.
 *
 * ── The four rules everything here follows ───────────────────────────────────────────────────────
 *
 * 1. THE SERVER IS THE GUARANTEE; THIS IS THE COURTESY. `MutualFundCardConfigBAL` re-runs every one of
 *    these checks before it writes a single row, and a save is all-or-nothing. What this file buys is
 *    the admin seeing the problem while they are still looking at the field that caused it, instead of
 *    one `BadRequest` string after pressing SAVE. That is also why {@link validateCardConfigItems}
 *    speaks the SERVER's sentences: the same mistake must not read as two different complaints
 *    depending on which side happened to catch it.
 *
 * 2. `configCode` IS AN IDENTITY, NOT A LABEL. It is what another part of the product will use to fetch
 *    a card ("give me TAX_FUND_FACTSHEET"), it is the business key `ReplaceAllAsync` matches existing
 *    rows on, and the column behind it is `VARCHAR(40)` — ASCII only. Hence {@link CODE_PATTERN}, hence
 *    the case-insensitive duplicate check: two codes differing only in case would collide in the
 *    database's own comparison and one row would silently eat the other.
 *
 * 3. NOTHING HERE THROWS. Both the validator and the parser run on EVERY KEYSTROKE, against a document
 *    that is half-typed most of the time — that is the normal state of a JSON textarea, not an error
 *    state. A blank box is "nothing yet", never a stack trace that takes the dialog down mid-edit.
 *
 * 4. A CARD IS AN ENVELOPE — a JSON OBJECT `{ type, version, refId, data }`. An array or a scalar parses
 *    perfectly well and is still not one, and handing either to the preview (whose `card` prop is
 *    `type: Object`) buys a Vue prop warning plus a card drawn from nothing. {@link parseCardConfig}
 *    refuses it out loud instead, exactly like `utils/taxCardPreview.js#parsePreviewCard` does for the
 *    card that comes back from the server.
 *
 * ── One deliberate inconsistency, so nobody "fixes" it ───────────────────────────────────────────
 * {@link parseCardConfig}'s message is THAI and {@link validateCardConfigItems}'s are ENGLISH. They are
 * two different messages in two different places: the first is the line Vuetify prints under the JSON
 * textarea, and it is the wording the admin already knows from `TaxQuestionItemV2Dialog.jsonError`
 * ("JSON ไม่ถูกต้อง: …"); the second is the form-level summary of a list, on a page (`/MutualFund`)
 * whose UI is English throughout and whose server echoes English back.
 */

/**
 * The one shape a `configCode` may take: A–Z, 0–9 and underscore, 1–40 characters.
 *
 * Mirrors `MutualFundCardConfigBAL`'s regex and the `VARCHAR(40)` column under it. No flags — a `g`
 * flag would make `.test()` stateful (it carries `lastIndex` between calls) and this constant is
 * exported for callers to use directly, including inside a Vuetify `:rules` array that runs it per
 * keystroke.
 */
export const CODE_PATTERN = /^[A-Z0-9_]{1,40}$/;

/**
 * How long one card document may be, in characters — 128 KB, the same ceiling as
 * `TaxCardTemplateV2.MaxTemplateChars`.
 *
 * Counted in JS string length (UTF-16 code units), which is what `NVARCHAR(MAX)` stores and what
 * .NET's `string.Length` counts on the other side, so both sides draw the line in the same place.
 */
export const MAX_CONFIG_CHARS = 131072;

/** How long a `configName` may be — matches `ConfigName NVARCHAR(200)`. */
export const MAX_NAME_CHARS = 200;

/** How long the derived `CardType` may be — matches `CardType VARCHAR(60)`. */
export const MAX_CARD_TYPE_CHARS = 60;

/**
 * A brand-new, empty row for the list.
 *
 * `id: 0` is what marks it as an INSERT to the server, `isActive: true` because an admin who just
 * pressed ADD wants the thing they are about to write, and `sortOrder: 0` because the real number is
 * assigned by {@link applySortOrder} from the row's POSITION — never typed, never trusted from a
 * client (see rule below).
 *
 * @param {string|number} rowKey stable identity for `<draggable item-key>`; it must NOT be the array
 *   index, or reordering would re-key every row and Vue would rebuild the DOM instead of moving it
 * @returns {{rowKey: *, id: number, configCode: string, configName: string, configJson: string,
 *   sortOrder: number, isActive: boolean}}
 */
export function blankCardConfigItem(rowKey) {
  return {
    rowKey,
    id: 0,
    configCode: '',
    configName: '',
    configJson: '',
    sortOrder: 0,
    isActive: true,
  };
}

/**
 * Read one item's JSON box into the envelope the preview component takes.
 *
 * Fail-soft by contract (rule 3): it REPORTS, it never raises, because it is called from a computed
 * that re-runs on every character the admin types.
 *
 * A BLANK box is not an error — it is a row nobody has filled in yet, and saying "invalid JSON" at
 * someone who has typed nothing is noise. {@link validateCardConfigItems} is the one that insists a
 * row eventually has a document, and it does so at SAVE time.
 *
 * @param {?string} raw the textarea's current contents, verbatim
 * @returns {{card: ?Object, error: ?string}} both keys always present — the caller destructures blind
 */
export function parseCardConfig(raw) {
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (text === '') return { card: null, error: null };

  let doc;
  try {
    doc = JSON.parse(text);
  } catch (err) {
    // The exact wording `TaxQuestionItemV2Dialog.jsonError` prints under its JSON boxes. The parser's
    // own message rides along because it is the only part that says WHERE the document broke.
    return { card: null, error: 'JSON ไม่ถูกต้อง: ' + ((err && err.message) || 'รูปแบบไม่ถูกต้อง') };
  }

  // Rule 4: valid JSON is not the same thing as a card.
  if (doc === null || typeof doc !== 'object' || Array.isArray(doc)) {
    return { card: null, error: 'JSON ไม่ถูกต้อง: ต้องเป็น JSON object (ไม่ใช่ array หรือค่าเดี่ยว)' };
  }
  return { card: doc, error: null };
}

/**
 * The card's `type` — the key that decides WHICH renderer draws it, and the value the server stores in
 * `CardType` so other features can query the library without parsing every row's JSON.
 *
 * Anything that is not a non-blank string is `''` — "this document does not say what it is" — and the
 * preview answers that with its unsupported-card branch rather than guessing. That includes a numeric
 * or object `type`, which is a payload bug worth seeing, not worth coercing into a plausible label.
 *
 * @param {?Object} card from {@link parseCardConfig}
 * @returns {string} e.g. 'TAX_FUND_FACTSHEET', or '' when absent/blank/not a string
 */
export function detectCardType(card) {
  if (!card || typeof card !== 'object' || Array.isArray(card)) return '';
  const type = card.type;
  return typeof type === 'string' ? type.trim() : '';
}

/**
 * The card's `refId` — the envelope's secondary discriminator ('type8' / 'type9' / 'type10').
 *
 * It is NOT what picks the renderer (`type` is), but it is the only thing that tells two cards sharing
 * a `type` apart — `TAX_SUMMARY` vs `FILING_CHOICE` are distinguished by `refId` alone in the tax
 * product — so the list shows it and this is the one reader of it.
 *
 * @param {?Object} card from {@link parseCardConfig}
 * @returns {string} '' when absent/blank/not a string
 */
export function detectRefId(card) {
  if (!card || typeof card !== 'object' || Array.isArray(card)) return '';
  const refId = card.refId;
  return typeof refId === 'string' ? refId.trim() : '';
}

/**
 * Every reason the current list cannot be saved, in the order an admin reading down the form would
 * hit them.
 *
 * 🔴 THE MESSAGES ARE THE SERVER'S, VERBATIM (rule 1).
 * `MutualFundCardConfigBAL.ValidateAndNormalizeRequest` throws these exact sentences — same prefix
 * (`Row {n}`, numbered from 1 the way the admin counts), same parenthetical code, same nouns
 * (`configCode` / `configName` / `configJson`, the wire names, not prose) — so the admin sees ONE
 * wording whichever side rejected the save. If a rule changes on one side it has to change on both:
 * a client that is merely LOOSER than the server is survivable (the server still refuses), a client
 * that is STRICTER blocks a save the server would happily have taken.
 *
 * The checks also run IN THE SERVER'S ORDER within a row — code → name → JSON — so the first
 * complaint an admin sees is the first one the server would have raised.
 *
 * 🔴 ONE DELIBERATE DIFFERENCE. The server THROWS on the first bad row and stops; this collects every
 * problem in the list and returns them all. A `BadRequest` is one round-trip per mistake, but this
 * runs from a computed and can afford to show the whole picture at once, so an admin fixes the form
 * once. That is why a row can contribute more than one line here and never does on the server.
 *
 * An EMPTY list is valid: deleting every config is a thing an admin is allowed to do, and this list is
 * a library, not a required setting.
 *
 * @param {Array<Object>} items the dialog's live rows
 * @returns {string[]} empty ⇒ `canSave`
 */
export function validateCardConfigItems(items) {
  const rows = Array.isArray(items) ? items : [];
  const errors = [];
  const seen = new Set();

  rows.forEach((row, index) => {
    const item = row && typeof row === 'object' ? row : {};
    const rowNumber = index + 1;

    // ── configCode: the identity (rule 2) ────────────────────────────────────────────────────────
    // Upper-cased BEFORE the pattern is applied, exactly as the server normalises it, so an admin who
    // typed lower case is corrected rather than scolded.
    const code = String(item.configCode == null ? '' : item.configCode).trim().toUpperCase();

    // Every message after the code names the row BY its code, because that is how the server writes
    // them and how an admin finds the row in a list of twenty. When the code is the thing that is
    // missing there is nothing to name, so the parenthetical is dropped rather than printed empty —
    // a case the server can never reach, since it stops at the bad code.
    const where = code === '' ? `Row ${rowNumber}` : `Row ${rowNumber} (${code})`;

    if (code === '') {
      errors.push(`Row ${rowNumber} must contain a configCode.`);
    } else if (!CODE_PATTERN.test(code)) {
      errors.push(`Row ${rowNumber} code ${code} must use A-Z, 0-9 and underscore only, up to 40 characters.`);
    } else if (seen.has(code)) {
      // Case-insensitive by construction (both sides are upper-cased first): the database's own
      // comparison is case-insensitive too, so two rows differing only in case are ONE row to it.
      errors.push(`Row ${rowNumber} code ${code} was submitted more than once.`);
    } else {
      seen.add(code);
    }

    // ── configName: what a human reads in the list ───────────────────────────────────────────────
    const name = String(item.configName == null ? '' : item.configName).trim();
    if (name === '') {
      errors.push(`${where} must contain a configName.`);
    } else if (name.length > MAX_NAME_CHARS) {
      errors.push(`${where} has a configName longer than ${MAX_NAME_CHARS} characters.`);
    }

    // ── configJson: the payload ──────────────────────────────────────────────────────────────────
    // Here, unlike in `parseCardConfig`, a blank box IS an error: this runs when the admin asks to
    // save, and a row with no document is a row that renders nothing.
    const json = typeof item.configJson === 'string' ? item.configJson : '';
    const trimmed = json.trim();
    if (trimmed === '') {
      errors.push(`${where} must contain a configJson.`);
      return;
    }
    // Measured on the TRIMMED text, because trimmed is what the server stores (`item.ConfigJson.Trim()`)
    // and therefore what the column has to hold. Counting the raw text would refuse a document the
    // server would have taken — the "stricter than the server" failure the rule above warns about.
    if (trimmed.length > MAX_CONFIG_CHARS) {
      errors.push(`${where} has a configJson longer than ${MAX_CONFIG_CHARS} characters.`);
      return;
    }
    // Parsed here rather than through `parseCardConfig` for one reason: the server quotes its parser's
    // own message ("what and where"), and `parseCardConfig`'s Thai sentence is the line printed UNDER
    // the textarea, a different message for a different place (see the note at the top of this file).
    let doc;
    try {
      doc = JSON.parse(trimmed);
    } catch (err) {
      errors.push(`${where} contains invalid JSON: ${(err && err.message) || 'the document could not be parsed'}`);
      return;
    }
    if (doc === null || typeof doc !== 'object' || Array.isArray(doc)) {
      errors.push(`${where} must contain a JSON object, not an array or a single value.`);
    }
  });

  return errors;
}

/**
 * Renumber `sortOrder` 1..n from each row's CURRENT POSITION.
 *
 * 🔴 POSITION IS THE ONLY SOURCE OF ORDER. The list is reordered by drag and by ↑/↓, neither of which
 * touches `sortOrder`; leaving the old numbers behind would make the array's order and the saved order
 * disagree, and the disagreement would only surface on the next page load. The server renumbers again
 * on save for the same reason — it does not trust the client's numbers either — so this exists to keep
 * what is ON SCREEN honest between now and then.
 *
 * Mutates in place AND returns the same array (the shape `TaxMobileProductSettingsDialog` established),
 * so it can be used as the setter of a `<draggable v-model>` computed.
 *
 * @param {Array<Object>} items
 * @returns {Array<Object>} the same array, renumbered
 */
export function applySortOrder(items) {
  if (!Array.isArray(items)) return items;
  items.forEach((item, index) => {
    if (item && typeof item === 'object') item.sortOrder = index + 1;
  });
  return items;
}

export default {
  CODE_PATTERN,
  MAX_CONFIG_CHARS,
  MAX_NAME_CHARS,
  MAX_CARD_TYPE_CHARS,
  blankCardConfigItem,
  parseCardConfig,
  detectCardType,
  detectRefId,
  validateCardConfigItems,
  applySortOrder,
};
